import { Button, SegmentedControl } from "@blueprintjs/core"
import React from "react"
import {
  ControlThresholds,
  CorrectionEntry,
  NavControlResult,
  PositionDiff,
  ReconciliationResult,
  ShareClassNavResult,
  ShareCountControlResult,
} from "../../../../../api/reconciliation"
import { StatusFilter } from "../useReconciliation"
import {
  ControlCountValue,
  NavControlCell,
  ShareClassCell,
  ShareClassCode,
  ShareClassCommentInput,
  ShareClassHeaderCell,
  ShareClassMessage,
  ShareClassSharesInput,
  ShareClassTable,
  ShareCountCell,
  ShareCountCommentInput,
  SummaryBarContainer,
  SummaryDivider,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  ClickableValue,
} from "../Reconciliation.styled"
import {
  correctionKey,
  describeNavControl,
  formatBp,
  formatCorrectionHistory,
  formatNav4dp,
  formatNumber,
  pageVerdict,
  PriceGapBreaches,
  ShareClassEdit,
  ShareCountAcceptanceEdit,
  shareCountState,
  unacceptedShareCountBreaches,
} from "../reconciliation.utils"

interface SummaryBarTotals {
  total_market_value_a: number
  total_market_value_b: number
  total_market_value_diff: number
  nav_per_share_a: number | null
  nav_per_share_b: number | null
}

interface SummaryBarProps {
  result: ReconciliationResult
  totals?: SummaryBarTotals | null
  statusFilter: StatusFilter
  onStatusFilterChange: (filter: StatusFilter) => void
  // Editable shares outstanding (#1631). Absent handler → read-only strip.
  shareClassEdits?: Map<string, ShareClassEdit>
  onShareClassEdit?: (
    code: string,
    field: "nombre_parts" | "comment",
    value: unknown,
  ) => void
  correctionHistory?: Map<string, CorrectionEntry[]>
  // Share-count acceptances pending submit (CONTEXT.md, "Share-count
  // acceptance"), keyed by class code. Absent handler → the mismatch is
  // shown, the accept action is not.
  shareCountAcceptances?: Map<string, ShareCountAcceptanceEdit>
  onShareCountAcceptance?: (code: string, acceptance: ShareCountAcceptanceEdit | null) => void
  // The per-class rows as the operator sees them — the compare result's
  // classes with any shares edit applied and, after a row value edit, the
  // Scient NAVs recomputed from the edited totals (effectiveShareClasses,
  // owned by the page hook; the same rows the submit echo persists).
  effectiveClasses: ShareClassNavResult[] | null
  // The NAV control per class as the operator sees it (operatorNavControls,
  // owned by the page hook): the server's decision, re-evaluated by the same
  // formula only where an edit changed a class's figures. The strip and the
  // submit confirmation read the same list.
  navControls: NavControlResult[]
  // The thresholds the server's controls ran with (the page hook's
  // thresholds); null when the server ran no controls.
  thresholds: ControlThresholds | null
  // The price-gap breaches as the operator sees them (the page hook's
  // priceGapBreaches): the breach tally and the breaches filter read them,
  // the submit confirmation the same ones. Absent when the server ran no
  // controls.
  priceGapBreaches?: PriceGapBreaches
  // The rows as the operator sees them (the page hook's displayDiffs): each
  // row's price-gap control is the server's, re-evaluated only after a
  // price edit. The verdict reads these rows, the same ones the grid renders.
  displayDiffs: PositionDiff[]
}

const formatShares = (n: number | null): string =>
  n === null ? "—" : n.toLocaleString("en-US")

const formatDelta = (n: number | null): string =>
  n === null ? "—" : `${n > 0 ? "+" : ""}${n.toFixed(4)}`

// The NAV control column (#1748): the server's result for the class, or an
// empty slot when no control ran for it. Unverified is shown as such, in
// the breach colour — it is not a pass.
const NavControlStatusCell: React.FC<{ ctrl?: NavControlResult }> = ({ ctrl }) => {
  if (!ctrl) return <ShareClassCell>—</ShareClassCell>
  return (
    <NavControlCell status={ctrl.status} title={describeNavControl(ctrl) ?? undefined}>
      {ctrl.status === "unverified" ? "unverified" : formatBp(ctrl.gap_bp)}
    </NavControlCell>
  )
}

interface ShareClassStripProps {
  classes: ShareClassNavResult[]
  // The NAV control per class as the operator sees it (operatorNavControls)
  // and the thresholds the server applied, shown with the column.
  navControls: NavControlResult[]
  thresholds: ControlThresholds | null
  // The share-count control per class: the server's result, never
  // re-evaluated — an edit to the count moves neither the file's figure
  // nor the known one, so the breach stands until accepted.
  shareCountControls: ShareCountControlResult[]
  shareClassEdits?: Map<string, ShareClassEdit>
  onShareClassEdit?: SummaryBarProps["onShareClassEdit"]
  correctionHistory?: Map<string, CorrectionEntry[]>
  shareCountAcceptances?: Map<string, ShareCountAcceptanceEdit>
  onShareCountAcceptance?: SummaryBarProps["onShareCountAcceptance"]
}

const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === "Enter") (e.target as HTMLInputElement).blur()
}

// Shares outstanding is the one editable figure in the strip (#1631):
// confirming a new count rescales the class NAVs live, and the edit lands
// as a correction on submit. Without an edit handler the cell stays a span.
const SharesCell: React.FC<{
  cls: ShareClassNavResult
  edit?: ShareClassEdit
  onEdit?: SummaryBarProps["onShareClassEdit"]
  history?: CorrectionEntry[]
}> = ({ cls, edit, onEdit, history }) => {
  const title = history?.length ? formatCorrectionHistory(history) : undefined
  if (!onEdit) {
    return <ShareClassCell title={title}>{formatShares(cls.nombre_parts)}</ShareClassCell>
  }
  const edited = edit?.nombre_parts !== undefined
  return (
    <ShareClassSharesInput
      // Remount when the effective value changes so the uncontrolled input
      // picks up externally applied edits without fighting the typist.
      key={cls.nombre_parts}
      defaultValue={cls.nombre_parts === null ? "" : String(cls.nombre_parts)}
      edited={edited}
      data-edited={edited || undefined}
      title={title}
      aria-label={`${cls.code} shares outstanding`}
      onBlur={e => onEdit(cls.code, "nombre_parts", e.target.value)}
      onKeyDown={blurOnEnter}
    />
  )
}

// The share-count control's cell (#1751): nothing when no control ran for
// the class (a feedless fund, the first day); the file count against the
// known one while a breach stands, with the accept action — the one
// gesture that clears it — and, once accepted, the acceptance's comment
// and a way to withdraw it; the operator's count against the file's when
// the edit is the breach, which nothing but reverting it clears. Without
// an acceptance handler the mismatch is still shown. The state is
// shareCountState's.
const ShareCountControlCell: React.FC<{
  ctrl?: ShareCountControlResult
  acceptance?: ShareCountAcceptanceEdit
  edit?: ShareClassEdit
  onAcceptance?: SummaryBarProps["onShareCountAcceptance"]
}> = ({ ctrl, acceptance, edit, onAcceptance }) => {
  if (!ctrl) return <ShareClassCell>—</ShareClassCell>
  // The acceptance counts whether or not the cell is editable — the same
  // reading the verdict and the submit gate take.
  const state = shareCountState(ctrl, acceptance, edit)
  if (state === "passed") return <ShareCountCell state="passed">matches</ShareCountCell>
  const file = formatShares(ctrl.file_nombre_parts)
  const known = formatShares(ctrl.known_nombre_parts)
  if (state === "unverified") {
    return <ShareCountCell state="unverified">{`no file count (known ${known})`}</ShareCountCell>
  }
  if (state === "edited") {
    return (
      <ShareCountCell state="edited">
        {`edited ${formatShares(edit?.nombre_parts ?? null)} ≠ file ${file}`}
      </ShareCountCell>
    )
  }
  if (state === "accepted") {
    return (
      <ShareCountCell state="accepted">
        <span>{`${file} accepted (known ${known})`}</span>
        {onAcceptance && (
          <>
            <ShareCountCommentInput
              placeholder="comment…"
              defaultValue={acceptance?.comment ?? ""}
              aria-label={`${ctrl.code} acceptance comment`}
              onBlur={e => onAcceptance(ctrl.code, { comment: e.target.value })}
              onKeyDown={blurOnEnter}
            />
            <Button small minimal text="Withdraw" onClick={() => onAcceptance(ctrl.code, null)} />
          </>
        )}
      </ShareCountCell>
    )
  }
  return (
    <ShareCountCell state="breached">
      <span>{`file ${file} ≠ known ${known}`}</span>
      {onAcceptance && (
        <Button
          small
          intent="warning"
          text="Accept new count"
          onClick={() => onAcceptance(ctrl.code, { comment: "" })}
        />
      )}
    </ShareCountCell>
  )
}

const ShareClassStrip: React.FC<ShareClassStripProps> = ({
  classes,
  navControls,
  thresholds,
  shareCountControls,
  shareClassEdits,
  onShareClassEdit,
  correctionHistory,
  shareCountAcceptances,
  onShareCountAcceptance,
}) => {
  const navControlByCode = new Map(navControls.map(ctrl => [ctrl.code, ctrl]))
  const shareCountByCode = new Map(shareCountControls.map(ctrl => [ctrl.code, ctrl]))
  // The threshold the control ran with, shown with the column so a reading
  // of the strip knows which rule applied that day.
  const navHeader = thresholds
    ? `NAV control (≤ ${thresholds.nav_threshold_bp} bp)`
    : "NAV control"
  return (
    <ShareClassTable role="table" aria-label="NAV per share class">
      {/* header row: labels once, not per class */}
      <span />
      <ShareClassHeaderCell>Shares</ShareClassHeaderCell>
      <ShareClassHeaderCell>Scient NAV</ShareClassHeaderCell>
      <ShareClassHeaderCell>Caceis NAV</ShareClassHeaderCell>
      <ShareClassHeaderCell>Caceis VL</ShareClassHeaderCell>
      {/* The 5bp split residual: a method diagnostic, not the verdict. */}
      <ShareClassHeaderCell>Δ vs VL</ShareClassHeaderCell>
      <ShareClassHeaderCell>{navHeader}</ShareClassHeaderCell>
      <ShareClassHeaderCell>Share count</ShareClassHeaderCell>
      <span />
      {classes.map(cls => {
        const edit = shareClassEdits?.get(cls.code)
        return (
          <React.Fragment key={cls.code}>
            <ShareClassCode>{cls.code}</ShareClassCode>
            <SharesCell
              cls={cls}
              edit={edit}
              onEdit={onShareClassEdit}
              history={correctionHistory?.get(correctionKey(cls.code, "nombre_parts"))}
            />
            <ShareClassCell>{formatNav4dp(cls.nav_per_share_a)}</ShareClassCell>
            <ShareClassCell>{formatNav4dp(cls.nav_per_share_b)}</ShareClassCell>
            <ShareClassCell>{formatNav4dp(cls.vl_parts_caceis)}</ShareClassCell>
            <ShareClassCell>{formatDelta(cls.diff_vs_caceis)}</ShareClassCell>
            <NavControlStatusCell ctrl={navControlByCode.get(cls.code)} />
            <ShareCountControlCell
              ctrl={shareCountByCode.get(cls.code)}
              acceptance={shareCountAcceptances?.get(cls.code)}
              edit={edit}
              onAcceptance={onShareCountAcceptance}
            />
            {edit?.nombre_parts !== undefined && onShareClassEdit ? (
              <ShareClassCommentInput
                key={`${cls.code}-comment`}
                placeholder="comment…"
                defaultValue={edit.comment ?? ""}
                aria-label={`${cls.code} correction comment`}
                onBlur={e => onShareClassEdit(cls.code, "comment", e.target.value)}
                onKeyDown={blurOnEnter}
              />
            ) : (
              <span />
            )}
          </React.Fragment>
        )
      })}
    </ShareClassTable>
  )
}

// The price-gap control's tally: how many rows breached, and apart from
// them how many could not be evaluated — unverified is not passed.
const describePriceGapTally = (breached: number, unverified: number): string => {
  const parts = [
    ...(breached > 0 ? [`${breached} breached`] : []),
    ...(unverified > 0 ? [`${unverified} unverified`] : []),
  ]
  return parts.length > 0 ? parts.join(", ") : "none"
}

const SummaryBar: React.FC<SummaryBarProps> = ({
  result,
  totals,
  statusFilter,
  onStatusFilterChange,
  shareClassEdits = new Map(),
  onShareClassEdit,
  correctionHistory,
  shareCountAcceptances = new Map(),
  onShareCountAcceptance,
  effectiveClasses,
  navControls,
  thresholds,
  priceGapBreaches,
  displayDiffs,
}) => {
  const totalA = totals?.total_market_value_a ?? result.total_market_value_a
  const totalB = totals?.total_market_value_b ?? result.total_market_value_b
  const totalDiff = totals?.total_market_value_diff ?? result.total_market_value_diff
  const mvDiffIsNonZero = totalDiff !== 0

  // The price-gap control over the rows as the operator sees them: shown,
  // with the thresholds it ran with, only when the server ran the controls.
  const priceGapRows = priceGapBreaches?.rows ?? []
  const breachedCount = priceGapRows.filter(d => d.price_gap_status === "breached").length
  const unverifiedCount = priceGapRows.length - breachedCount

  const options = [
    { label: `All (${result.diffs.length})`, value: "all" as StatusFilter },
    { label: `Paired (${result.count_paired})`, value: "paired" as StatusFilter },
    {
      label: `${result.source_a_name} Only (${result.count_a_only})`,
      value: "source_a_only" as StatusFilter,
    },
    {
      label: `${result.source_b_name} Only (${result.count_b_only})`,
      value: "source_b_only" as StatusFilter,
    },
    ...(priceGapBreaches
      ? [
          {
            label: `Breaches (${priceGapRows.length})`,
            value: "price_gap_breaches" as StatusFilter,
          },
        ]
      : []),
  ]

  const { share_class_metadata, share_class_warning } = result

  // The page verdict, carried by the bar's left accent, reads the controls
  // as the operator sees them — not a tie computed here (CONTEXT.md,
  // "Reconciliation controls"). A share-count breach counts until accepted.
  const shareCountControls = result.controls?.share_count_controls ?? []
  const verdict = pageVerdict(result, {
    navControls,
    diffs: displayDiffs,
    shareCountBreaches: unacceptedShareCountBreaches(
      result.controls,
      shareCountAcceptances,
      shareClassEdits,
    ),
  })

  const shareClassBlock = (() => {
    // Warning takes priority: something the operator must act on (e.g. a class
    // in the CACEIS file with no ShareClass configuration yet).
    if (share_class_warning) {
      return <ShareClassMessage>⚠ {share_class_warning}</ShareClassMessage>
    }

    // Computed (single- or multi-class) → the per-class table, with the NAV
    // control's result per class: the red gap is the whole message, the
    // class row beside it carries both NAVs and the header the threshold.
    if (effectiveClasses !== null && effectiveClasses.length > 0) {
      return (
        <SummaryItem>
          <ShareClassStrip
            classes={effectiveClasses}
            navControls={navControls}
            thresholds={thresholds}
            shareCountControls={shareCountControls}
            shareClassEdits={shareClassEdits}
            onShareClassEdit={onShareClassEdit}
            correctionHistory={correctionHistory}
            shareCountAcceptances={shareCountAcceptances}
            onShareCountAcceptance={onShareCountAcceptance}
          />
        </SummaryItem>
      )
    }

    // No class data at all (unmapped fund, or unknown shares outstanding)
    if (share_class_metadata === null) return null

    // GP3 format (empty metadata, null classes)
    if (share_class_metadata.length === 0) {
      return (
        <ShareClassMessage>
          Per-class NAV requires the INV format — GP3 files don&apos;t carry per-class figures.
        </ShareClassMessage>
      )
    }

    // Metadata present but no prior-day split computed
    return (
      <ShareClassMessage>
        Per-class NAV needs the previous day&apos;s split — submit a recon for the prior NAV date
        first.
      </ShareClassMessage>
    )
  })()

  return (
    <SummaryBarContainer verdict={verdict}>
      <SummaryItem>
        <SummaryLabel>NAV Date</SummaryLabel>
        <SummaryValue>{result.nav_date}</SummaryValue>
      </SummaryItem>

      <SummaryDivider />

      <SummaryItem>
        <SummaryLabel>{result.source_a_name} MV</SummaryLabel>
        <SummaryValue>{formatNumber(totalA)}</SummaryValue>
      </SummaryItem>

      <SummaryItem>
        <SummaryLabel>{result.source_b_name} MV</SummaryLabel>
        <SummaryValue>{formatNumber(totalB)}</SummaryValue>
      </SummaryItem>

      <SummaryItem>
        <SummaryLabel>MV Diff</SummaryLabel>
        <SummaryValue style={mvDiffIsNonZero ? { color: "#E76A6E" } : undefined}>
          {formatNumber(totalDiff)}
        </SummaryValue>
      </SummaryItem>

      {shareClassBlock && (
        <>
          <SummaryDivider />
          {shareClassBlock}
        </>
      )}

      {priceGapBreaches && (
        <>
          <SummaryDivider />
          <SummaryItem>
            <SummaryLabel>
              Price gap (&gt; {priceGapBreaches.thresholds.price_gap_threshold_pct}% and &gt;{" "}
              {priceGapBreaches.thresholds.nav_share_threshold_pct}% of NAV)
            </SummaryLabel>
            {priceGapRows.length > 0 ? (
              // One click on the tally selects the Breaches filter: the grid
              // becomes the price-gap table of the breached securities.
              <ControlCountValue breached>
                <ClickableValue
                  role="button"
                  title="Show the breached securities"
                  onClick={() => onStatusFilterChange("price_gap_breaches")}
                >
                  {describePriceGapTally(breachedCount, unverifiedCount)}
                </ClickableValue>
              </ControlCountValue>
            ) : (
              <ControlCountValue breached={false}>
                {describePriceGapTally(breachedCount, unverifiedCount)}
              </ControlCountValue>
            )}
          </SummaryItem>
        </>
      )}

      <SummaryDivider />

      <SegmentedControl
        small
        intent="primary"
        value={statusFilter}
        onValueChange={value => onStatusFilterChange(value as StatusFilter)}
        options={options}
      />
    </SummaryBarContainer>
  )
}

export default SummaryBar
